// =============================================================================
// Admin Analytics — server drilldown state
// =============================================================================
// Everything a server-paginated drilldown's query depends on — open, facet
// selections, the dialog's date range, and the page — lives with the widget
// that owns the query, so this hook hands the widget the state and the dialog
// the setters. Any filter change goes back to page 1; closing resets it all.
//
// The dialog reads the state as it changes; the widget's queries read `query`,
// in which selection and facet-search changes settle for a moment first, so a
// quick multi-select (or a typed search) sends one request, not one per click
// or keystroke.
// =============================================================================
import { useCallback, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";

import { CalendarDateRange } from "@fronterahealth/frontera-ui-components";

import { FilterValues } from "@pages/AdminAnalytics/components/DrilldownFilters";
import { FacetSearch, PageState } from "@pages/AdminAnalytics/data/analyticsFilters";
import { PAGE_SIZE } from "@pages/VideoAnalytics/helpers";

/** How long selection and facet-search changes settle before the queries see them. */
export const SELECTION_DEBOUNCE_MS = 300;

/** Display labels per facet key: value → label (e.g. a provider id → their name). */
export type FacetLabels = Record<string, Record<string, string>>;

export interface ServerDrilldownSeed {
  /** Facet selections to start from — e.g. the status bar or provider row that was clicked. */
  selections?: FilterValues;
  /** Display labels for seeded values, per facet key (e.g. a provider id's name), until the facets load. */
  labels?: FacetLabels;
  dateRange?: CalendarDateRange;
}

export interface ServerDrilldown {
  open: boolean;
  /** Bumps every time the dialog opens, so per-session state (e.g. placeholder facets) never leaks across opens. */
  session: number;
  selections: FilterValues;
  /**
   * Labels for every value selected this session — from the seed, and from the
   * options at the moment a value was picked — so a chip never falls back to a
   * raw id when the options are reloading or no longer include that value.
   */
  knownLabels: FacetLabels;
  /** What the open value list is searching for on the server; empty when none is. */
  facetSearch: FacetSearch;
  /** The dialog's own date range; `undefined` = the widget's range. */
  dateRange: CalendarDateRange | undefined;
  page: PageState;
  /**
   * What the widget's queries should use. Opening, paging and date changes
   * apply at once; a selection or facet-search change waits
   * `SELECTION_DEBOUNCE_MS`, and until then the queries keep the last settled
   * selections, search and page.
   */
  query: { selections: FilterValues; facetSearch: FacetSearch; page: PageState };
  openWith: (seed?: ServerDrilldownSeed) => void;
  setOpen: (open: boolean) => void;
  /** `labels`: display labels for the newly selected values, merged into `knownLabels`. */
  setSelections: (selections: FilterValues, labels?: FacetLabels) => void;
  setFacetSearch: (search: FacetSearch) => void;
  setDateRange: (range: CalendarDateRange | undefined) => void;
  setPage: (page: number, pageSize: number) => void;
}

interface DrilldownState {
  open: boolean;
  session: number;
  selections: FilterValues;
  knownLabels: FacetLabels;
  facetSearch: FacetSearch;
  dateRange: CalendarDateRange | undefined;
  page: PageState;
}

const NO_SEARCH: FacetSearch = {};

const CLOSED: Omit<DrilldownState, "session"> = {
  open: false,
  selections: {},
  knownLabels: {},
  facetSearch: NO_SEARCH,
  dateRange: undefined,
  page: { page: 1, pageSize: PAGE_SIZE },
};

const mergeLabels = (known: FacetLabels, added: FacetLabels | undefined): FacetLabels => {
  if (!added) return known;
  const merged = { ...known };
  Object.entries(added).forEach(([key, labels]) => {
    merged[key] = { ...merged[key], ...labels };
  });
  return merged;
};

export const useServerDrilldown = (): ServerDrilldown => {
  const [state, setState] = useState<DrilldownState>({ ...CLOSED, session: 0 });

  const openWith = useCallback(
    (seed: ServerDrilldownSeed = {}) =>
      setState((s) => ({
        ...CLOSED,
        open: true,
        session: s.session + 1,
        selections: seed.selections ?? {},
        knownLabels: seed.labels ?? {},
        dateRange: seed.dateRange,
      })),
    [],
  );
  const setOpen = useCallback(
    (open: boolean) => (open ? openWith() : setState((s) => ({ ...CLOSED, session: s.session }))),
    [openWith],
  );
  const setSelections = useCallback(
    (selections: FilterValues, labels?: FacetLabels) =>
      setState((s) => ({
        ...s,
        selections,
        knownLabels: mergeLabels(s.knownLabels, labels),
        page: { ...s.page, page: 1 },
      })),
    [],
  );
  // Clearing an already-empty search (every value list closing) changes nothing.
  const setFacetSearch = useCallback(
    (search: FacetSearch) =>
      setState((s) => {
        const empty = !Object.values(search).some(Boolean);
        if (empty && s.facetSearch === NO_SEARCH) return s;
        return { ...s, facetSearch: empty ? NO_SEARCH : search };
      }),
    [],
  );
  const setDateRange = useCallback(
    (dateRange: CalendarDateRange | undefined) => setState((s) => ({ ...s, dateRange, page: { ...s.page, page: 1 } })),
    [],
  );
  const setPage = useCallback(
    (page: number, pageSize: number) => setState((s) => ({ ...s, page: { page, pageSize } })),
    [],
  );

  const current = useMemo(
    () => ({
      session: state.session,
      selections: state.selections,
      facetSearch: state.facetSearch,
      page: state.page,
    }),
    [state.session, state.selections, state.facetSearch, state.page],
  );
  const [settled] = useDebounce(current, SELECTION_DEBOUNCE_MS);
  // Only a selection or search change made in this session waits for the debounce.
  const pending =
    settled.session === current.session &&
    (settled.selections !== current.selections || settled.facetSearch !== current.facetSearch);
  const query = useMemo(() => {
    const { selections, facetSearch, page } = pending ? settled : current;
    return { selections, facetSearch, page };
  }, [pending, settled, current]);

  return useMemo(
    () => ({ ...state, query, openWith, setOpen, setSelections, setFacetSearch, setDateRange, setPage }),
    [state, query, openWith, setOpen, setSelections, setFacetSearch, setDateRange, setPage],
  );
};
